/**
 * Bootstrap — render-exempt file (test-spec §7.2): wiring only. All logic
 * lives in joinFlow.ts (tested), panels/ (tested), net/ (tested), shared
 * (100%), canvas/ (exempt). META-06 holds this file to 200 lines; if an
 * edit here starts making decisions, it belongs in one of those modules.
 */

import {
  createRoomState,
  pointToCell,
  type Command,
  type RoomState,
} from "@asarimitsuha/table-core";
import { drawGridOverlay } from "./canvas/gridOverlay.js";
import { createMapSprite, updateMapSprite } from "./canvas/mapSprite.js";
import { createRenderer } from "./canvas/renderer.js";
import { createTokenLayer } from "./canvas/tokenSprite.js";
import { attachViewport } from "./canvas/viewport.js";
import { installTestHooks } from "./hooks.js";
import { createJoinFlow } from "./joinFlow.js";
import {
  connectToTable,
  sdkJoin,
  type ConnectionStatus,
  type TableConnection,
} from "./net/connection.js";
import {
  buildJoinPath,
  parseJoinParams,
  resolveMapForDisplay,
  serverEndpoint,
  serverFetch,
} from "./net/joinParams.js";
import { renderCreateForm, renderRoomInfo } from "./panels/lobby.js";
import { renderConnectionStatus } from "./panels/seatRoster.js";
import { createTablePanelMounts, renderTablePanels } from "./panels/tablePanels.js";

const panel = document.getElementById("panel") as HTMLElement;
const header = document.getElementById("status") as HTMLElement;
const mapMount = document.getElementById("map") as HTMLElement;
const noticeMount = document.getElementById("notices") as HTMLElement;
const modalMount = document.getElementById("modal") as HTMLElement;
const bannerMount = document.getElementById("connection-banner") as HTMLElement;

let connection: TableConnection | null = null;
let resolveReady: () => void;
const ready = new Promise<void>((resolve) => (resolveReady = resolve));

function currentState(): RoomState {
  return connection?.getState() ?? createRoomState("", "");
}
function currentStatus(): ConnectionStatus {
  return connection?.getStatus() ?? "connecting";
}

// Statically-eliminable block: Rollup drops it in production builds, which
// tree-shakes hooks.js entirely — META-03 requires the string "__vtt" to be
// absent from the shipped bundle, not merely un-executed.
if (import.meta.env.DEV) {
  installTestHooks({
    ready,
    getState: currentState,
    getConnection: currentStatus,
    getPendingCommandCount: () => connection?.getPendingCommandCount() ?? 0,
    sendRawCommand: (command) => connection?.sendRawCommand(command) ?? null,
  });
}

const flow = createJoinFlow({
  // Server-relative API paths follow VITE_SERVER_URL when the client is
  // hosted on a different origin than the game server.
  fetchFn: serverFetch(import.meta.env, (...args) => fetch(...args)),
  storage: localStorage,
  connect: (options, onSeatAssigned) =>
    connectToTable(options, { join: sdkJoin, onSeatAssigned }),
  endpoint: () => serverEndpoint(location, import.meta.env),
  origin: () => location.origin,
  replaceUrl: (path) => history.replaceState(null, "", path),
  panel,
  notices: noticeMount,
  onConnected: (table, code, gmKey) => {
    connection = table;
    return mountTable(table, code, gmKey);
  },
});

async function mountTable(
  table: TableConnection,
  code: string,
  gmKey: string | null,
): Promise<void> {
  panel.replaceChildren();
  renderRoomInfo(panel, { code, joinUrl: location.origin + buildJoinPath(code) });
  const mounts = createTablePanelMounts(panel, {
    modal: modalMount,
    header,
    banner: bannerMount,
  });

  const { app, world } = await createRenderer(mapMount);
  attachViewport(app, world);
  const mapSprite = createMapSprite(world);
  let grid: { destroy(): void } | null = null;
  let gridKey = "";
  let selectedTokenId: string | null = null;
  let currentGmKey = gmKey;
  let mapPanelDrawn = false;

  const send = (command: Command) => table.sendCommand(command);
  const tokenLayer = createTokenLayer(world, {
    canDrag: (tokenId) => {
      const state = table.getState();
      const seat = table.getSeatId() !== null ? state.seats[table.getSeatId()!] : undefined;
      const token = state.tokens[tokenId];
      if (!seat || !token) return false;
      return seat.role === "gm" || token.ownerSeatId === seat.id;
    },
    onDrop: (tokenId, worldX, worldY) => {
      const state = table.getState();
      const cell = pointToCell(
        { x: worldX, y: worldY },
        { cellSizePx: state.map.cellSizePx, feetPerCell: state.map.feetPerCell },
      );
      send({ type: "moveToken", tokenId, col: cell.col, row: cell.row });
      rerender(); // snap back immediately if the command was refused locally
    },
    onSelect: (tokenId) => {
      selectedTokenId = tokenId;
      rerender();
    },
  });

  flow.attachTableEvents(table);

  const rerender = (): void => {
    const state = currentState();
    const result = renderTablePanels(
      mounts,
      {
        state,
        ownSeatId: table.getSeatId(),
        selectedTokenId,
        rekeyError: flow.getRekeyError(),
        connectionStatus: currentStatus(),
        bannerSuppressed: flow.isBannerSuppressed(),
        drawMapPanel: !mapPanelDrawn,
      },
      {
        send,
        onSelectToken: (tokenId) => {
          selectedTokenId = tokenId;
          rerender();
        },
        onSubmitGmKey: (key) => {
          currentGmKey = key;
          flow.submitGmKey(table, code, key);
        },
        onUploadMapImage: (file) =>
          void flow.uploadMapImage(code, currentGmKey, file, send),
      },
    );
    if (result.mapPanelDrawn) mapPanelDrawn = true;

    // Canvas projection: redraw grid when its dimensions change.
    const nextGridKey = `${state.map.widthCells}x${state.map.heightCells}@${state.map.cellSizePx}`;
    if (state.id !== "" && nextGridKey !== gridKey) {
      gridKey = nextGridKey;
      grid?.destroy();
      grid = drawGridOverlay(world, state.map);
      resolveReady();
    }
    void updateMapSprite(mapSprite, resolveMapForDisplay(state.map, import.meta.env));
    tokenLayer.sync(state);
  };
  table.onChange(rerender);
  rerender();
}

async function start(): Promise<void> {
  renderConnectionStatus(header, "connecting");
  const params = parseJoinParams(location.search, location.hash);

  if (params.code) {
    await flow.enterTable(params.code, params.gmKey);
    return;
  }

  renderConnectionStatus(header, "closed");
  renderCreateForm(panel, {
    onCreate: (tableName) => {
      void flow.createTable(tableName);
    },
  });
}

void start();
